import {ProgressiveLoadingStatus, type ProgressiveDiffEntry} from '../../types/progressive-diff-types'
import {buildProgressiveDiffEntries} from '../../utils/build-progressive-diff-entries'
import {loadDiffEntriesForPaths} from '../../page-data/loaders/use-diff-entries'
import {identifyDiffEntriesToLoad} from '../../utils/identify-diff-entries-to-load'
import {throttle} from '@github/mini-throttle'
import type {StateCreator} from 'zustand'
import type {ComparisonInfoSlice} from './comparison-info'
import type {DiffSummariesSlice, DiffSummariesSliceOpts} from './diff-summaries'

export interface ProgressiveDiffsSlice {
  entries: ProgressiveDiffEntry[]
  selectedPathDigest: string | undefined

  // Actions
  loadMore: (startingAt: ProgressiveDiffEntry) => Promise<void>
  loadSelectedEntry: (selectedEntry: ProgressiveDiffEntry) => Promise<void>
  loadEntriesStartingAt: (startingAt: ProgressiveDiffEntry) => void | Promise<void>
}

export type ProgressiveDiffSliceOpts = {
  pathName: string
  prxFilesLiteThrottle?: boolean | undefined
  prxFilesMediumThrottle?: boolean | undefined
  initialLoadedPathDigests: Set<string>
  selectedPathDigest: string | undefined
} & Pick<DiffSummariesSliceOpts, 'initialDiffSummaries'>

export const createProgressiveDiffsSlice =
  ({
    pathName,
    prxFilesLiteThrottle,
    prxFilesMediumThrottle,
    initialDiffSummaries,
    initialLoadedPathDigests,
    selectedPathDigest,
  }: ProgressiveDiffSliceOpts): StateCreator<
    ProgressiveDiffsSlice & ComparisonInfoSlice & DiffSummariesSlice,
    [['zustand/devtools', never]],
    [],
    ProgressiveDiffsSlice
  > =>
  (set, get) => {
    let throttleTime: number

    if (prxFilesMediumThrottle) {
      throttleTime = 100
    } else if (prxFilesLiteThrottle) {
      throttleTime = 50
    } else {
      throttleTime = 0
    }

    const entries = buildProgressiveDiffEntries({
      diffSummaries: initialDiffSummaries,
      loadedPathDigests: initialLoadedPathDigests,
      loadingPathDigests: new Set<string>(),
      selectedPathDigest,
    })

    return {
      entries,
      selectedPathDigest,
      loadSelectedEntry: async (selectedEntry: ProgressiveDiffEntry) => {
        const previouslyLoadedPathDigests = get()
          .entries.filter(e => e.loadingStatus === ProgressiveLoadingStatus.Loaded)
          .map(e => e.pathDigest)
        const previouslyLoadingPathDigests = get()
          .entries.filter(e => e.loadingStatus === ProgressiveLoadingStatus.Loading)
          .map(e => e.pathDigest)

        set(
          state => ({
            entries: buildProgressiveDiffEntries({
              diffSummaries: state.diffSummaries,
              loadedPathDigests: new Set(previouslyLoadedPathDigests),
              loadingPathDigests: new Set([...previouslyLoadingPathDigests, selectedEntry.pathDigest]),
              selectedPathDigest: state.selectedPathDigest,
            }),
          }),
          undefined,
          'loadSelectedEntry#preFetch',
        )

        // Fetch more diff entries
        const newlyLoadedDiffEntries = await loadDiffEntriesForPaths({
          basePath: pathName,
          paths: [selectedEntry.path],
          ...get().comparisonInfo,
        })

        const newEntry = newlyLoadedDiffEntries?.[0]

        if (!newEntry) {
          // We are not handling retry logic currently
          return
        }

        const currentLoadedPathDigests = [
          ...get()
            .entries.filter(e => e.loadingStatus === ProgressiveLoadingStatus.Loaded)
            .map(e => e.pathDigest),
          newEntry.pathDigest,
        ]
        const currentLoadingPathDigests = get()
          .entries.filter(e => e.loadingStatus === ProgressiveLoadingStatus.Loading)
          .filter(e => e.pathDigest !== newEntry.pathDigest)
          .map(e => e.pathDigest)

        const updatedEntries = buildProgressiveDiffEntries({
          diffSummaries: get().diffSummaries,
          loadedPathDigests: new Set(currentLoadedPathDigests),
          loadingPathDigests: new Set(currentLoadingPathDigests),
          selectedPathDigest: get().selectedPathDigest,
        })

        return set(
          {
            entries: updatedEntries,
          },
          undefined,
          'loadSelectedEntry#postFetch',
        )
      },
      loadEntriesStartingAt: throttle(async (startingAt: ProgressiveDiffEntry) => {
        let pathsToFetch: string[] = []
        let pathDigestsToFetch: string[] = []
        const previouslyLoadedPathDigests = get()
          .entries.filter(e => e.loadingStatus === ProgressiveLoadingStatus.Loaded)
          .map(e => e.pathDigest)
        const previouslyLoadingPathDigests = get()
          .entries.filter(e => e.loadingStatus === ProgressiveLoadingStatus.Loading)
          .map(e => e.pathDigest)

        const {pathsToLoad, pathDigestsToLoad} = identifyDiffEntriesToLoad({
          progressiveDiffEntries: get().entries,
          startingAt,
        })

        if (pathsToLoad.size === 0) return

        pathsToFetch = Array.from(pathsToLoad)
        pathDigestsToFetch = Array.from(pathDigestsToLoad)

        set(
          state => ({
            entries: buildProgressiveDiffEntries({
              diffSummaries: state.diffSummaries,
              loadedPathDigests: new Set(previouslyLoadedPathDigests),
              loadingPathDigests: new Set([...previouslyLoadingPathDigests, ...pathDigestsToFetch]),
              selectedPathDigest: state.selectedPathDigest,
            }),
          }),
          undefined,
          'loadEntriesStartingAt#preFetch',
        )

        const newlyLoadedDiffEntries = await loadDiffEntriesForPaths({
          basePath: pathName,
          paths: pathsToFetch,
          ...get().comparisonInfo,
        })

        const noApiData = !newlyLoadedDiffEntries || newlyLoadedDiffEntries.length === 0
        if (noApiData) {
          const currentLoadedPathDigests = [
            ...get()
              .entries.filter(e => e.loadingStatus === ProgressiveLoadingStatus.Loaded)
              .map(e => e.pathDigest),
          ]
          const currentLoadingPathDigests = get()
            .entries.filter(e => e.loadingStatus === ProgressiveLoadingStatus.Loading)
            .map(e => e.pathDigest)

          return set(
            {
              entries: buildProgressiveDiffEntries({
                diffSummaries: get().diffSummaries,
                loadedPathDigests: new Set(currentLoadedPathDigests),
                loadingPathDigests: new Set(currentLoadingPathDigests),
                selectedPathDigest: get().selectedPathDigest,
              }),
            },
            undefined,
            'loadEntriesStartingAt#postFetch#noAPIData',
          )
        }

        const currentLoadedPathDigests = [
          ...get()
            .entries.filter(e => e.loadingStatus === ProgressiveLoadingStatus.Loaded)
            .map(e => e.pathDigest),
          ...newlyLoadedDiffEntries.map(e => e.pathDigest),
        ]
        const currentLoadingPathDigests = get()
          .entries.filter(e => e.loadingStatus === ProgressiveLoadingStatus.Loading)
          .filter(e => !newlyLoadedDiffEntries.map(ne => ne.pathDigest).includes(e.pathDigest))
          .map(e => e.pathDigest)

        set(
          (state: ProgressiveDiffsSlice) => ({
            ...state,
            entries: buildProgressiveDiffEntries({
              diffSummaries: get().diffSummaries,
              loadedPathDigests: new Set(currentLoadedPathDigests),
              loadingPathDigests: new Set(currentLoadingPathDigests),
              selectedPathDigest: get().selectedPathDigest,
            }),
          }),
          undefined,
          'loadEntriesStartingAt#postFetch',
        )
      }, throttleTime),
      loadMore: async (startingAt: ProgressiveDiffEntry) => {
        const selectedEntryToLoadFirst = get().entries.find(
          e => e.loadSolo && e.loadingStatus === ProgressiveLoadingStatus.NotLoaded,
        )

        if (selectedEntryToLoadFirst) {
          return get().loadSelectedEntry(selectedEntryToLoadFirst)
        } else {
          return get().loadEntriesStartingAt(startingAt)
        }
      },
    }
  }
